import { useEffect, useState, useCallback } from "react";
import { api, coverUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STUDIO } from "@/constants/testIds";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Sparkles, Wand2, Music4, ImageIcon, FileText, Package, X, Pencil, Trash2, DollarSign, TrendingUp, Download } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareButton from "@/components/ShareButton";
import PayoutsPanel from "@/components/PayoutsPanel";
import ProductSparkline from "@/components/ProductSparkline";
import ReferralsPanel from "@/components/ReferralsPanel";
import { API } from "@/lib/api";

const CATEGORIES = [
  { key: "ebook", label: "E-Book (PDF)", accept: ".pdf,application/pdf", icon: FileText },
  { key: "podcast", label: "Podcast (audio)", accept: "audio/*", icon: Music4 },
  { key: "music", label: "Music (audio)", accept: "audio/*", icon: Music4 },
  { key: "image", label: "Image", accept: "image/*", icon: ImageIcon },
  { key: "video", label: "Video", accept: "video/*", icon: Wand2 },
  { key: "bundle", label: "Bundle (.zip)", accept: ".zip,application/zip,application/x-zip-compressed", icon: Package },
];

const MAX_PRODUCT_MB = 200;

export default function CreatorStudio() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [myProducts, setMyProducts] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/studio");
  }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const [prods, earn] = await Promise.all([
        api.get("/creator/products"),
        api.get("/creator/earnings"),
      ]);
      setMyProducts(prods.data);
      setEarnings(earn.data);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? It will be hidden from the store immediately. Existing buyers keep access.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Drop hidden");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00] flex items-center gap-3">
            // Creator Studio
            {earnings?.is_verified && <VerifiedBadge testId={STUDIO.verifiedBadge} />}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
            Your drops. Your revenue.
          </h1>
          <p className="mt-3 text-white/60 max-w-lg">
            You keep <span className="text-[#CCFF00] font-mono">{earnings ? (100 - earnings.platform_fee_pct).toFixed(0) : "90"}%</span> of every sale.
            Reach 3 paid sales to unlock the Verified badge.
          </p>
        </div>
        <button
          data-testid={STUDIO.newProductBtn}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#FF3366] text-white font-mono uppercase tracking-[0.2em] text-xs px-5 py-3 hover:bg-[#FF3366]/90"
        >
          <Plus className="w-4 h-4" /> New Drop
        </button>
      </div>

      {/* Payouts (Stripe Connect) */}
      <PayoutsPanel />

      {/* Earnings dashboard */}
      <section className="mb-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <EarningsCard
          testId={STUDIO.earningsGross}
          label="Gross Revenue"
          value={earnings ? `$${earnings.gross_revenue.toFixed(2)}` : "$0.00"}
          icon={<DollarSign className="w-4 h-4" />}
        />
        <EarningsCard
          testId={STUDIO.earningsNet}
          label={`Net (You Keep)`}
          value={earnings ? `$${earnings.net_earnings.toFixed(2)}` : "$0.00"}
          highlight
          icon={<TrendingUp className="w-4 h-4" />}
        />
        <EarningsCard
          testId={STUDIO.earningsSold}
          label="Units Sold"
          value={earnings?.total_sold ?? 0}
        />
        <EarningsCard
          testId={STUDIO.earningsFeePct}
          label="Platform Fee"
          value={earnings ? `${earnings.platform_fee_pct.toFixed(0)}%` : "—"}
          sublabel={earnings ? `You keep ${(100 - earnings.platform_fee_pct).toFixed(0)}%` : ""}
        />
      </section>

      {earnings && <ReferralsPanel />}

      {earnings && earnings.recent_sales.length > 0 && (
        <section className="mb-14">
          <div className="flex items-end justify-between gap-4 mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">// Recent sales</div>
            <button
              data-testid="studio-download-csv-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/creator/payouts.csv`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("dd_token") || ""}` },
                    credentials: "include",
                  });
                  if (!res.ok) throw new Error("Download failed");
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `digital-drops-payouts-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch (_e) {
                  toast.error("Could not download CSV");
                }
              }}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 px-3 py-2"
            >
              <Download className="w-3 h-3" /> Download CSV
            </button>
          </div>
          <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/10">
            {earnings.recent_sales.map((s) => (
              <div key={s.purchase_id} className="p-4 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{s.product_title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {s.at ? new Date(s.at).toLocaleString() : "—"}
                  </div>
                </div>
                <div className="font-mono text-[#CCFF00]">+${s.amount.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid={STUDIO.productsList}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">// Your drops</div>
        {myProducts.length === 0 ? (
          <div className="border border-dashed border-white/15 py-16 text-center">
            <p className="text-white/60">No drops yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {myProducts.map((p) => (
              <div key={p.id} className="drop-card border border-white/10 hover:border-white/30 bg-[#0A0A0A]">
                <div className="aspect-[16/10] bg-[#141414] overflow-hidden relative">
                  <Link to={`/p/${p.id}`}>
                    <img src={coverUrl(p.cover_storage_path)} className="w-full h-full object-cover" alt="" />
                  </Link>
                  {!p.is_active && (
                    <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 text-white/80 border border-white/20 px-2 py-1">
                      HIDDEN
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{p.category}</div>
                  <Link to={`/p/${p.id}`} className="font-display font-bold text-lg mt-1 truncate block hover:text-[#CCFF00]">{p.title}</Link>
                  <div className="mt-3 flex items-center justify-between text-xs font-mono">
                    <span className="text-[#CCFF00]">${p.price.toFixed(2)}</span>
                    <span className="text-white/60">{p.sold_count}/{p.total_stock} sold</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">
                      <span>// 30d sales</span>
                    </div>
                    <ProductSparkline productId={p.id} days={30} />
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={STUDIO.editBtn(p.id)}
                      onClick={() => setEditing(p)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/20 hover:border-white/50 font-mono text-[10px] uppercase tracking-[0.2em] py-2"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <ShareButton
                      testId={`studio-share-${p.id}`}
                      url={`${window.location.origin}/p/${p.id}`}
                      shareUrl={`${window.location.origin}/api/share/product/${p.id}`}
                      title={`${p.title} · by ${p.creator_name}`}
                      description={p.description}
                      variant="compact"
                    />
                    <button
                      data-testid={STUDIO.deleteBtn(p.id)}
                      onClick={() => del(p)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#FF3366]/50 text-[#FF3366] hover:bg-[#FF3366]/10 font-mono text-[10px] uppercase tracking-[0.2em] py-2"
                    >
                      <Trash2 className="w-3 h-3" /> {p.is_active ? "Hide" : "Hidden"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {open && <NewDropDialog onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); toast.success("Drop published"); }} />}
      {editing && <EditDropDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); toast.success("Drop updated"); }} />}
    </div>
  );
}

function EarningsCard({ testId, label, value, sublabel, highlight, icon }) {
  return (
    <div
      data-testid={testId}
      className={`border p-5 ${highlight ? "border-[#CCFF00]/60 bg-[#CCFF00]/5" : "border-white/10 bg-[#0A0A0A]"}`}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
        {icon}
        {label}
      </div>
      <div className={`font-mono font-bold text-2xl mt-2 ${highlight ? "text-[#CCFF00]" : "text-white"}`}>
        {value}
      </div>
      {sublabel && <div className="font-mono text-[10px] text-white/50 mt-1">{sublabel}</div>}
    </div>
  );
}

function EditDropDialog({ product, onClose, onSaved }) {
  const [title, setTitle] = useState(product.title);
  const [description, setDescription] = useState(product.description);
  const [price, setPrice] = useState(product.price);
  const [stock, setStock] = useState(product.total_stock);
  const [dropEndsAt, setDropEndsAt] = useState(product.drop_ends_at ? toLocalInput(product.drop_ends_at) : "");
  const [isActive, setIsActive] = useState(product.is_active);
  const [tags, setTags] = useState((product.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const parseTags = (s) => s.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 10);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/products/${product.id}`, {
        title, description,
        price: parseFloat(price),
        total_stock: parseInt(stock, 10),
        drop_ends_at: dropEndsAt ? new Date(dropEndsAt).toISOString() : "",
        is_active: isActive,
        tags: parseTags(tags),
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center overflow-y-auto p-4">
      <div data-testid={STUDIO.editDialog} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/15 my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">// Edit drop</div>
            <div className="font-display font-black text-xl mt-1">{product.title}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          <Field label="Title">
            <input required className="brutal-input w-full px-3 py-2.5" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea required rows={5} className="brutal-input w-full px-3 py-2.5" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Tags (comma-separated · max 10)">
            <input
              className="brutal-input w-full px-3 py-2.5"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="synth, ambient, edition"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Price (USD)">
              <input required type="number" step="0.01" min="0.5" className="brutal-input w-full px-3 py-2.5" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
            <Field label={`Edition size (already sold: ${product.sold_count})`}>
              <input required type="number" min={product.sold_count || 1} className="brutal-input w-full px-3 py-2.5" value={stock} onChange={(e) => setStock(e.target.value)} />
            </Field>
            <Field label="Drop ends (optional)">
              <input type="datetime-local" className="brutal-input w-full px-3 py-2.5" value={dropEndsAt} onChange={(e) => setDropEndsAt(e.target.value)} />
            </Field>
            <Field label="Status">
              <label className="flex items-center gap-3 mt-2">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4 accent-[#CCFF00]" />
                <span className="font-mono text-xs uppercase tracking-[0.2em]">
                  {isActive ? "Active (visible in store)" : "Hidden"}
                </span>
              </label>
            </Field>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/20">Cancel</button>
            <button
              data-testid={STUDIO.editSaveBtn}
              type="submit"
              disabled={busy}
              className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 bg-[#CCFF00] text-black hover:opacity-90 disabled:opacity-40"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => (n < 10 ? "0" + n : n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewDropDialog({ onClose, onCreated }) {
  const [category, setCategory] = useState("ebook");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(9.99);
  const [stock, setStock] = useState(50);
  const [dropEndsAt, setDropEndsAt] = useState("");
  const [tags, setTags] = useState("");
  const [cover, setCover] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [aiOpen, setAiOpen] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cover) return toast.error("Please upload a cover image");
    if (!productFile) return toast.error("Please upload the product file");
    if (productFile.size > MAX_PRODUCT_MB * 1024 * 1024) {
      return toast.error(`Product file exceeds ${MAX_PRODUCT_MB} MB limit`);
    }
    setBusy(true);
    try {
      const coverUp = await upload(cover, "cover");
      const fileUp = await upload(productFile, "product");
      await api.post("/products", {
        title,
        description,
        category,
        price: parseFloat(price),
        total_stock: parseInt(stock, 10),
        drop_ends_at: dropEndsAt ? new Date(dropEndsAt).toISOString() : null,
        cover_storage_path: coverUp.storage_path,
        file_storage_path: fileUp.storage_path,
        file_original_name: fileUp.original_filename,
        file_size: fileUp.size,
        file_mime: fileUp.content_type,
        tags: tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 10),
        bundle_file_count: category === "bundle" ? fileUp.bundle_file_count : null,
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to publish");
    } finally { setBusy(false); }
  };

  const runAiText = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/text", { prompt: aiPrompt });
      setDescription((prev) => (prev ? prev + "\n\n" : "") + data.text);
      setAiOpen(null); setAiPrompt("");
    } catch (e) { toast.error("AI text failed"); }
    finally { setAiBusy(false); }
  };
  const runAiImage = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: aiPrompt });
      const res = await fetch(`${api.defaults.baseURL}/files/cover/${data.storage_path}`);
      const blob = await res.blob();
      const f = new File([blob], "ai-cover.png", { type: "image/png" });
      setCover(f);
      setAiOpen(null); setAiPrompt("");
      toast.success("AI cover ready");
    } catch (e) { toast.error("AI image failed"); }
    finally { setAiBusy(false); }
  };
  const runAiAudio = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/audio", { text: aiPrompt, voice: "alloy" });
      const res = await fetch(`${api.defaults.baseURL}/files/cover/${data.storage_path}`);
      const blob = await res.blob();
      const f = new File([blob], "ai-audio.mp3", { type: "audio/mpeg" });
      setProductFile(f);
      setAiOpen(null); setAiPrompt("");
      toast.success("AI audio ready");
    } catch (e) { toast.error("AI audio failed"); }
    finally { setAiBusy(false); }
  };

  const cat = CATEGORIES.find((c) => c.key === category);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center overflow-y-auto p-4">
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-white/15 my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">// Publish drop</div>
            <div className="font-display font-black text-xl mt-1">New Digital Drop</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={STUDIO.categorySelect + "-" + c.key}
                  onClick={() => { setCategory(c.key); setProductFile(null); }}
                  className={`text-left border p-3 ${category === c.key ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-white/15 hover:border-white/40"}`}
                >
                  <Icon className="w-4 h-4 mb-2" />
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em]">{c.label}</div>
                </button>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title">
              <input data-testid={STUDIO.titleInput} required className="brutal-input w-full px-3 py-2.5" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Price (USD)">
              <input data-testid={STUDIO.priceInput} required type="number" step="0.01" min="0.5" className="brutal-input w-full px-3 py-2.5" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
            <Field label="Edition size (stock)">
              <input data-testid={STUDIO.stockInput} required type="number" min="1" max="10000" className="brutal-input w-full px-3 py-2.5" value={stock} onChange={(e) => setStock(e.target.value)} />
            </Field>
            <Field label="Drop ends (optional)">
              <input data-testid={STUDIO.dropDateInput} type="datetime-local" className="brutal-input w-full px-3 py-2.5" value={dropEndsAt} onChange={(e) => setDropEndsAt(e.target.value)} />
            </Field>
          </div>

          <Field label="Description">
            <div className="relative">
              <textarea
                data-testid={STUDIO.descInput}
                required
                rows={5}
                className="brutal-input w-full px-3 py-2.5"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <button
                type="button"
                data-testid={STUDIO.aiTextBtn}
                onClick={() => setAiOpen("text")}
                className="absolute right-2 top-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#CCFF00]/60 text-[#CCFF00] px-2 py-1 hover:bg-[#CCFF00]/10 flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> AI
              </button>
            </div>
          </Field>

          <Field label="Tags (comma-separated · max 10 · used for search & discovery)">
            <input
              data-testid="studio-tags-input"
              className="brutal-input w-full px-3 py-2.5"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="synth, ambient, edition, mystery"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cover image">
              <div className="flex items-center gap-2">
                <input
                  data-testid={STUDIO.coverUpload}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCover(e.target.files?.[0] || null)}
                  className="brutal-input w-full px-3 py-2 text-xs"
                />
                <button
                  type="button"
                  data-testid={STUDIO.aiImageBtn}
                  onClick={() => setAiOpen("image")}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#CCFF00]/60 text-[#CCFF00] px-2 py-2 hover:bg-[#CCFF00]/10 whitespace-nowrap"
                  title="Generate with AI"
                >
                  <Sparkles className="w-3 h-3 inline" /> AI
                </button>
              </div>
              {cover && <div className="mt-2 font-mono text-[10px] text-white/50 truncate">{cover.name || "AI-generated"} · {(cover.size/1024).toFixed(1)} KB</div>}
            </Field>
            <Field label={`Product file (${cat.label})`}>
              <div className="flex items-center gap-2">
                <input
                  data-testid={STUDIO.fileUpload}
                  type="file"
                  accept={cat.accept}
                  onChange={(e) => setProductFile(e.target.files?.[0] || null)}
                  className="brutal-input w-full px-3 py-2 text-xs"
                />
                {(category === "podcast" || category === "music") && (
                  <button
                    type="button"
                    data-testid={STUDIO.aiAudioBtn}
                    onClick={() => setAiOpen("audio")}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#CCFF00]/60 text-[#CCFF00] px-2 py-2 hover:bg-[#CCFF00]/10 whitespace-nowrap"
                  >
                    <Sparkles className="w-3 h-3 inline" /> TTS
                  </button>
                )}
              </div>
              {category === "bundle" && (
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                  // Zip up your folder first · max {MAX_PRODUCT_MB} MB · buyers download the whole archive
                </div>
              )}
              {productFile && <div className="mt-2 font-mono text-[10px] text-white/50 truncate">{productFile.name || "AI-generated"} · {(productFile.size/1024/1024).toFixed(2)} MB</div>}
            </Field>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/20">Cancel</button>
            <button
              data-testid={STUDIO.createBtn}
              type="submit"
              disabled={busy}
              className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 bg-[#FF3366] text-white hover:bg-[#FF3366]/90 disabled:opacity-40"
            >
              {busy ? "Publishing…" : "Publish drop"}
            </button>
          </div>
        </form>
      </div>

      {aiOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#CCFF00]/40 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
              // AI · {aiOpen}
            </div>
            <div className="font-display font-black text-xl mt-1 mb-4">
              {aiOpen === "text" ? "Generate description" : aiOpen === "image" ? "Generate cover" : "Generate voice audio"}
            </div>
            <textarea
              data-testid={STUDIO.aiPrompt}
              rows={4}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="brutal-input w-full px-3 py-2.5"
              placeholder={
                aiOpen === "text" ? "Describe your drop… (topic, tone, audience)"
                : aiOpen === "image" ? "e.g. abstract obsidian cover, magenta accents, brutalist"
                : "Enter script to convert to speech (English)…"
              }
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setAiOpen(null)} className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/20">Cancel</button>
              <button
                disabled={aiBusy || !aiPrompt.trim()}
                onClick={aiOpen === "text" ? runAiText : aiOpen === "image" ? runAiImage : runAiAudio}
                className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 bg-[#CCFF00] text-black disabled:opacity-40"
              >
                {aiBusy ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</div>
      {children}
    </label>
  );
}
